/**
 * ProtectedRoute.tsx — Role-based route guard for Shield.
 *
 * Usage:
 *   <ProtectedRoute>...</ProtectedRoute>                     any authenticated user
 *   <ProtectedRoute minRole="engineer">...</ProtectedRoute>  engineer or super_admin
 *   <ProtectedRoute minRole="super_admin">...</ProtectedRoute> super_admin only
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

import { useAuth, hasMinRole } from '../context/AuthContext';
import NotFound from '../pages/NotFound';

// Known protected route prefixes — unauthenticated access to OTHER paths shows 404
const KNOWN_AUTH_PATHS = [
  '/dashboard', '/scans', '/alerts', '/shadow-ai', '/auto-fix',
  '/tickets', '/profile', '/billing', '/compliance', '/admin',
];

interface ProtectedRouteProps {
  children: React.ReactNode;
  minRole?: 'customer' | 'engineer' | 'super_admin';
}

export default function ProtectedRoute({ children, minRole = 'customer' }: ProtectedRouteProps) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <Typography color="text.secondary">Loading…</Typography>
      </Box>
    );
  }

  if (!user) {
    const isKnownAuthPath = KNOWN_AUTH_PATHS.some(p => location.pathname.startsWith(p));
    if (!isKnownAuthPath) {
      return <NotFound />;
    }
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!hasMinRole(user, minRole)) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: 2,
        }}
      >
        <LockOutlinedIcon sx={{ fontSize: 64, color: 'error.main' }} />
        <Typography variant="h5" fontWeight={700}>
          Access Denied
        </Typography>
        <Typography color="text.secondary" textAlign="center" maxWidth={400}>
          You don't have permission to view this page. This area requires{' '}
          <strong>{minRole === 'super_admin' ? 'Super Admin' : 'Engineer'}</strong> access.
        </Typography>
        <Button variant="outlined" href="/dashboard">
          Back to Dashboard
        </Button>
      </Box>
    );
  }

  return <>{children}</>;
}
